import { useEffect, useMemo, useState } from "react";
import type { GameReview, Message, PairingStatus, ReviewPairing, ReviewRound } from "backend/types";
import { api } from "./api";
import { Avatar, usePoll } from "./ui";
import { Leaderboard } from "./Leaderboard";

const PAIRING_BADGE: Record<PairingStatus, string> = {
  chatting: "bg-wa/15 text-wa",
  resolved: "bg-gold/25 text-amber-700",
  timedout: "bg-black/10 text-ink/50",
};

export function ReviewPage({ gameId, onLeave }: { gameId: string; onLeave: () => void }) {
  const { data, error } = usePoll(() => api.getReview(gameId), 3000, [gameId]);
  const review: GameReview | null = data?.review ?? null;

  const [selectedRoundIndex, setSelectedRoundIndex] = useState(0);
  const [selectedPairingId, setSelectedPairingId] = useState<string | null>(null);

  useEffect(() => {
    if (!review) return;
    const round = review.rounds[selectedRoundIndex] ?? review.rounds[review.rounds.length - 1];
    const newRoundIdx = review.rounds.findIndex((r) => r.roundId === round?.roundId);
    if (newRoundIdx !== selectedRoundIndex) setSelectedRoundIndex(Math.max(0, newRoundIdx));

    const pairing = round?.pairings.find((p) => p.pairingId === selectedPairingId) ?? round?.pairings[0];
    if (pairing && pairing.pairingId !== selectedPairingId) {
      setSelectedPairingId(pairing.pairingId);
    }
  }, [review, selectedRoundIndex, selectedPairingId]);

  const round: ReviewRound | null = useMemo(() => {
    if (!review) return null;
    return review.rounds[selectedRoundIndex] ?? review.rounds[0] ?? null;
  }, [review, selectedRoundIndex]);

  const pairing: ReviewPairing | null = useMemo(() => {
    if (!round) return null;
    return round.pairings.find((p) => p.pairingId === selectedPairingId) ?? round.pairings[0] ?? null;
  }, [round, selectedPairingId]);

  if (error) {
    return (
      <div className="p-6">
        <p className="text-red-500">{error}</p>
        <button onClick={onLeave} className="mt-3 text-sm text-wa hover:underline">
          ← Back to rooms
        </button>
      </div>
    );
  }

  if (!review) return <div className="p-6 text-ink/40">Loading review…</div>;

  if (review.rounds.length === 0) {
    return (
      <div className="mx-auto w-full max-w-2xl p-6">
        <PageHeader review={review} onLeave={onLeave} />
        <div className="text-center text-ink/50 mt-10">No rounds have been played yet.</div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-49px)] bg-parchment/60">
      <div className="mx-auto w-full max-w-5xl p-4 space-y-4">
        <PageHeader review={review} onLeave={onLeave} />

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-ink/50">Round</span>
          {review.rounds.map((r, i) => (
            <button
              key={r.roundId}
              onClick={() => {
                setSelectedRoundIndex(i);
                setSelectedPairingId(review.rounds[i]?.pairings[0]?.pairingId ?? null);
              }}
              className={
                "text-sm rounded-full px-3 py-1 border " +
                (selectedRoundIndex === i
                  ? "bg-wa text-white border-wa"
                  : "bg-white border-black/10 hover:bg-parchment-deep/50")
              }
            >
              {r.index} · {r.mode === "presale" ? "fresh" : "after-sale"}
            </button>
          ))}
        </div>

        <div className="flex flex-col md:flex-row gap-4 items-start">
          <div className="w-full md:w-72 shrink-0 space-y-2">
            <div className="text-xs uppercase tracking-wide text-ink/40">Conversations</div>
            <ul className="space-y-2">
              {round?.pairings.map((p) => (
                <li key={p.pairingId}>
                  <button
                    onClick={() => setSelectedPairingId(p.pairingId)}
                    className={
                      "w-full text-left rounded-xl border px-3 py-2 flex items-center gap-2 " +
                      (pairing?.pairingId === p.pairingId
                        ? "border-wa bg-wa/5"
                        : "border-black/5 bg-white hover:bg-parchment-deep/30")
                    }
                  >
                    <Avatar color={p.customer.color} size={22} />
                    <span className="text-sm truncate max-w-[5rem]">{p.customer.name}</span>
                    <span className="text-ink/30">↔</span>
                    <Avatar color={p.seller.color} size={22} />
                    <span className="text-sm truncate max-w-[5rem]">{p.seller.name}</span>
                    <span className={"text-[10px] uppercase rounded-full px-2 py-0.5 ml-auto " + PAIRING_BADGE[p.status]}>
                      {p.status}
                    </span>
                    <span className="text-[11px] text-ink/40 tabular-nums">{p.messages.length}💬</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex-1 min-w-0">
            {pairing ? (
              <div className="bg-white rounded-2xl border border-black/5 shadow-sm overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 bg-wa text-white">
                  <Avatar color={pairing.customer.color} size={36} ring />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">
                      {pairing.customer.name} ↔ {pairing.seller.name}
                    </div>
                    <div className="text-[11px] text-white/80">
                      {pairing.product} · round {round?.index}
                    </div>
                  </div>
                  <span
                    className={
                      "text-[10px] uppercase rounded-full px-2 py-0.5 bg-white/20 "
                    }
                  >
                    {pairing.status}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-parchment-deep/30 border-b border-black/5 text-sm">
                  <BriefingCard
                    title="Customer brief"
                    name={pairing.customer.name}
                    color={pairing.customer.color}
                    role="customer"
                    personaLabel={pairing.customerPersonaLabel}
                    persona={pairing.customerPersonaInstruction}
                    context={pairing.customerContext}
                    objectives={pairing.objectives}
                  />
                  <BriefingCard
                    title="Seller brief"
                    name={pairing.seller.name}
                    color={pairing.seller.color}
                    role="seller"
                    personaLabel={pairing.sellerPersonaLabel}
                    persona={pairing.sellerPersonaInstruction}
                    context={`Selling ${pairing.product}`}
                    objectives={[]}
                  />
                </div>

                <div className="chat-bg p-3 space-y-1.5 min-h-[12rem] max-h-[60vh] overflow-y-auto">
                  {pairing.messages.length === 0 && (
                    <div className="text-center text-xs text-ink/40 mt-8">No messages in this conversation.</div>
                  )}
                  <MessageList messages={pairing.messages} pairing={pairing} />
                </div>

                <div className="border-t border-black/5 p-4 bg-parchment-deep/20">
                  <div className="text-xs uppercase tracking-wide text-ink/40 mb-2">Guess results</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <GuessResult
                      label="Customer guessed"
                      guess={pairing.customerGuess}
                      guesser={pairing.customer}
                      actual={pairing.seller}
                      players={review.players}
                    />
                    <GuessResult
                      label="Seller guessed"
                      guess={pairing.sellerGuess}
                      guesser={pairing.seller}
                      actual={pairing.customer}
                      players={review.players}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-ink/50">Select a conversation to review.</div>
            )}
          </div>
        </div>

        <Leaderboard gameId={review.gameId} />
      </div>
    </div>
  );
}

function PageHeader({ review, onLeave }: { review: GameReview; onLeave: () => void }) {
  return (
    <div className="flex items-center justify-between">
      <button onClick={onLeave} className="text-sm text-ink/50 hover:text-ink">
        ← Rooms
      </button>
      <h1 className="font-display text-xl font-bold truncate px-2">{review.name}</h1>
      <span className="text-[11px] uppercase tracking-wide bg-wa/15 text-wa rounded-full px-2 py-0.5">
        host review
      </span>
    </div>
  );
}

function BriefingCard({
  title,
  name,
  color,
  role,
  personaLabel,
  persona,
  context,
  objectives,
}: {
  title: string;
  name: string;
  color: string;
  role: "customer" | "seller";
  personaLabel: string;
  persona: string;
  context: string;
  objectives: string[];
}) {
  return (
    <div className="rounded-2xl bg-white border border-black/5 p-3">
      <div className="flex items-center gap-2 mb-2">
        <Avatar color={color} size={22} />
        <span className="text-xs uppercase tracking-wide text-ink/40">{title}</span>
      </div>
      <div className="text-sm font-semibold mb-1">
        {name} · {role === "customer" ? "🛒 Customer" : "🧑‍💼 Seller"}
      </div>
      <div className="text-xs text-spice font-semibold mb-1">{personaLabel}</div>
      <p className="text-xs text-ink/70 leading-relaxed mb-2">{persona}</p>
      <div className="rounded-xl bg-parchment-deep/50 p-2 text-xs text-ink/70">
        <span className="font-semibold">Context: </span>
        {context}
      </div>
      {objectives.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-ink/70 list-disc list-inside">
          {objectives.map((o, i) => (
            <li key={i}>{o}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MessageList({ messages, pairing }: { messages: Message[]; pairing: ReviewPairing }) {
  return (
    <>
      {messages.map((m) => {
        if (m.kind === "event") {
          return (
            <div key={m.id} className="flex justify-center my-1">
              <span className="text-[11px] text-ink/50 bg-gold/20 rounded-full px-3 py-0.5">{m.body}</span>
            </div>
          );
        }
        const fromCustomer = m.senderPlayerId === pairing.customer.id;
        return (
          <div key={m.id} className={"flex " + (fromCustomer ? "justify-start" : "justify-end")}>
            <div
              className={
                "max-w-[78%] rounded-2xl px-3 py-1.5 text-sm shadow-sm " +
                (fromCustomer ? "bg-white rounded-bl-sm" : "bg-bubble-out rounded-br-sm")
              }
            >
              <div className="text-[10px] text-ink/40 mb-0.5">
                {fromCustomer ? pairing.customer.name : pairing.seller.name}
              </div>
              <div className="whitespace-pre-wrap break-words">{m.body}</div>
              <div className="text-[10px] text-ink/40 text-right mt-0.5">
                {new Date(m.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}

function GuessResult({
  label,
  guess,
  guesser,
  actual,
  players,
}: {
  label: string;
  guess: { guesserPlayerId: string; guessedPlayerId: string; correct: boolean } | null;
  guesser: { id: string; name: string; color: string };
  actual: { id: string; name: string; color: string };
  players: { playerId: string; name: string; color: string }[];
}) {
  const guessedName = guess ? players.find((p) => p.playerId === guess.guessedPlayerId)?.name ?? "unknown" : null;
  return (
    <div className="rounded-2xl bg-white border border-black/5 p-3 text-sm">
      <div className="text-xs text-ink/40 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <Avatar color={guesser.color} size={24} />
        <span className="font-medium truncate">{guesser.name}</span>
      </div>
      {guess ? (
        <div className="mt-2 flex items-center gap-2 text-xs">
          <span className="text-ink/70">guessed</span>
          <Avatar color={players.find((p) => p.playerId === guess.guessedPlayerId)?.color ?? "#ccc"} size={18} />
          <span className="font-medium">{guessedName}</span>
          <span className={guess.correct ? "text-wa font-semibold" : "text-spice font-semibold"}>
            {guess.correct ? "✓ correct" : "✗ wrong"}
          </span>
        </div>
      ) : (
        <div className="mt-2 text-xs text-ink/50">No guess</div>
      )}
      <div className="mt-2 text-xs text-ink/50">
        Actual partner: <span className="font-medium text-ink/80">{actual.name}</span>
      </div>
    </div>
  );
}
